/**
 * Fan-out search across all four entity kinds. Debounces the query by
 * 200 ms, runs 4 parallel React Query calls, and merges results sorted
 * by best-substring-match then recency.
 */

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { notesApi } from "../api/notes";
import { tasksApi } from "../api/tasks";
import { calendarApi } from "../api/calendar";
import { habitsApi } from "../api/habits";
import { recipesApi } from "../api/recipes";
import { shoppingApi } from "../api/shopping";
import { useApi } from "../auth/AuthProvider";
import type { MentionKind } from "./MentionContext";

export interface MentionResult {
  kind: MentionKind;
  id: string;
  title: string;
}

const LIMIT = 5;
// A wide enough date range for "recent" calendar events (+/- 90 days)
function calRange(): { from: string; to: string } {
  const now = new Date();
  const from = new Date(now);
  from.setDate(now.getDate() - 90);
  const to = new Date(now);
  to.setDate(now.getDate() + 90);
  const fmt = (d: Date): string => d.toISOString().slice(0, 10);
  return { from: fmt(from), to: fmt(to) };
}

function sortResults(results: MentionResult[], query: string): MentionResult[] {
  const q = query.toLowerCase();
  return [...results].sort((a, b) => {
    // Best substring match first (lower index = better)
    const aIdx = q ? a.title.toLowerCase().indexOf(q) : 0;
    const bIdx = q ? b.title.toLowerCase().indexOf(q) : 0;
    const aScore = aIdx === -1 ? 1000 : aIdx;
    const bScore = bIdx === -1 ? 1000 : bIdx;
    return aScore - bScore;
  });
}

export function useMentionSearch(query: string): {
  results: MentionResult[];
  isLoading: boolean;
} {
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const client = useApi();

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query), 200);
    return () => clearTimeout(id);
  }, [query]);

  const q = debouncedQuery.trim();
  const range = calRange();

  const notesQ = useQuery<MentionResult[]>({
    queryKey: ["mention-search", "note", q],
    queryFn: async () => {
      const res = await notesApi(client).list({ q: q || undefined, limit: LIMIT });
      return res.notes.slice(0, LIMIT).map((n) => {
        const firstLine = n.content.split("\n")[0]?.trim().replace(/\s+/g, " ") ?? "";
        const title = firstLine.slice(0, 60) || "(empty note)";
        return { kind: "note" as MentionKind, id: n.id, title };
      });
    },
    staleTime: 30_000,
  });

  const tasksQ = useQuery<MentionResult[]>({
    queryKey: ["mention-search", "task", q],
    queryFn: async () => {
      const res = await tasksApi(client).search({ q: q || undefined, limit: LIMIT });
      return res.tasks.slice(0, LIMIT).map((t) => ({
        kind: "task" as MentionKind,
        id: t.id,
        title: t.title,
      }));
    },
    staleTime: 30_000,
  });

  const eventsQ = useQuery<MentionResult[]>({
    queryKey: ["mention-search", "event", q],
    queryFn: async () => {
      const res = await calendarApi(client).list({ ...range, q: q || undefined, expand: false });
      const sorted = [...res.events].sort(
        (a, b) => new Date(b.starts_at).getTime() - new Date(a.starts_at).getTime(),
      );
      return sorted.slice(0, LIMIT).map((e) => ({
        kind: "event" as MentionKind,
        id: e.id,
        title: e.title,
      }));
    },
    staleTime: 30_000,
  });

  const habitsQ = useQuery<MentionResult[]>({
    queryKey: ["mention-search", "habit", q],
    queryFn: async () => {
      const res = await habitsApi(client).list({ q: q || undefined });
      return res.habits.slice(0, LIMIT).map((h) => ({
        kind: "habit" as MentionKind,
        id: h.id,
        title: h.name,
      }));
    },
    staleTime: 30_000,
  });

  /** Shopping has no server-side search endpoint; pull the full list
   *  (lists + their embedded items) once, then filter + slice locally.
   *  Cached for 30 s like the others. */
  const shoppingQ = useQuery<MentionResult[]>({
    queryKey: ["mention-search", "shopping", q],
    queryFn: async () => {
      const all = await shoppingApi(client).listLists();
      const lower = q.toLowerCase();
      const lists: MentionResult[] = all.lists
        .filter((l) => lower === "" || l.name.toLowerCase().includes(lower))
        .slice(0, LIMIT)
        .map((l) => ({
          kind: "shopping_list" as MentionKind,
          id: l.id,
          title: l.name,
        }));
      const items: MentionResult[] = all.lists
        .flatMap((l) => l.items)
        .filter((i) => lower === "" || i.name.toLowerCase().includes(lower))
        .slice(0, LIMIT)
        .map((i) => ({
          kind: "shopping_item" as MentionKind,
          id: i.id,
          title: i.name,
        }));
      return [...lists, ...items];
    },
    staleTime: 30_000,
  });

  const recipesQ = useQuery<MentionResult[]>({
    queryKey: ["mention-search", "recipe", q],
    queryFn: async () => {
      const all = await recipesApi(client).list();
      const lower = q.toLowerCase();
      return all.recipes
        .filter((r) => lower === "" || r.title.toLowerCase().includes(lower))
        .slice(0, LIMIT)
        .map((r) => ({
          kind: "recipe" as MentionKind,
          id: r.id,
          title: r.title,
        }));
    },
    staleTime: 30_000,
  });

  const isLoading =
    notesQ.isLoading ||
    tasksQ.isLoading ||
    eventsQ.isLoading ||
    habitsQ.isLoading ||
    shoppingQ.isLoading ||
    recipesQ.isLoading;

  const rawResults: MentionResult[] = [
    ...(notesQ.data ?? []),
    ...(tasksQ.data ?? []),
    ...(eventsQ.data ?? []),
    ...(habitsQ.data ?? []),
    ...(shoppingQ.data ?? []),
    ...(recipesQ.data ?? []),
  ];

  const results = sortResults(rawResults, q);

  return { results, isLoading };
}
